import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface LeadRow {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  company_name?: string;
  location?: string;
  source?: string;
  tags?: string;
  [key: string]: string | undefined;
}

interface ValidationIssue {
  index: number;
  isValid: boolean;
  dataQualityScore: number;
  issues: string[];
  suggestions?: {
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
  };
  potentialDuplicateOf?: number;
}

interface ValidationResult {
  validatedLeads: ValidationIssue[];
  batchSummary: {
    totalValid: number;
    totalInvalid: number;
    duplicatesFound: number;
    averageQualityScore: number;
    commonIssues: string[];
  };
  duplicatesInDatabase: number[];
}

export function useBulkLeadImport() {
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [parsedLeads, setParsedLeads] = useState<LeadRow[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const queryClient = useQueryClient();

  const parseCSV = (csvContent: string): LeadRow[] => {
    const lines = csvContent.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'));
    const leads: LeadRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
      const lead: LeadRow = {};
      
      headers.forEach((header, index) => {
        if (values[index]) {
          // Map common header variations to standard fields
          const normalizedHeader = normalizeHeader(header);
          lead[normalizedHeader] = values[index];
        }
      });

      if (lead.first_name || lead.last_name || lead.email) {
        leads.push(lead);
      }
    }

    return leads;
  };

  const normalizeHeader = (header: string): string => {
    const mappings: Record<string, string> = {
      'firstname': 'first_name',
      'first': 'first_name',
      'fname': 'first_name',
      'lastname': 'last_name',
      'last': 'last_name',
      'lname': 'last_name',
      'emailaddress': 'email',
      'email_address': 'email',
      'mail': 'email',
      'phonenumber': 'phone',
      'phone_number': 'phone',
      'telephone': 'phone',
      'mobile': 'phone',
      'company': 'company_name',
      'organization': 'company_name',
      'org': 'company_name',
      'city': 'location',
      'address': 'location',
      'country': 'location',
    };

    return mappings[header] || header;
  };

  const validateLeads = async (leads: LeadRow[]) => {
    if (leads.length === 0) {
      toast.error('No valid leads found in the file');
      return;
    }

    setIsValidating(true);
    setParsedLeads(leads);
    setValidationResult(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      const { data, error } = await supabase.functions.invoke('validate-csv-leads', {
        body: {
          leads,
          tenantId: profile.tenant_id,
          checkDuplicates: true,
        },
      });

      if (error) throw error;

      if (data?.success) {
        setValidationResult(data);
        toast.success(`Validated ${leads.length} leads`);
      } else {
        toast.error(data?.error || 'Validation failed');
      }
    } catch (err: any) {
      console.error('Validation error:', err);
      toast.error(err.message || 'Failed to validate leads');
    } finally {
      setIsValidating(false);
    }
  };

  const importLeads = async (options: { skipDuplicates: boolean; skipInvalid: boolean }) => {
    if (!parsedLeads.length) {
      toast.error('No leads to import');
      return;
    }

    setIsImporting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      // Filter leads based on options
      let leadsToImport = parsedLeads;
      
      if (validationResult && options.skipDuplicates) {
        const duplicateIndices = new Set([
          ...validationResult.duplicatesInDatabase,
          ...(validationResult.validatedLeads
            .filter(v => v.potentialDuplicateOf !== undefined)
            .map(v => v.index))
        ]);
        leadsToImport = leadsToImport.filter((_, i) => !duplicateIndices.has(i));
      }

      if (validationResult && options.skipInvalid) {
        const invalidIndices = new Set(
          validationResult.validatedLeads
            .filter(v => !v.isValid)
            .map(v => v.index)
        );
        leadsToImport = leadsToImport.filter((_, i) => !invalidIndices.has(i));
      }

      if (leadsToImport.length === 0) {
        toast.error('No valid leads to import after filtering');
        return;
      }

      // Transform leads for insertion
      const leadsForInsert = leadsToImport.map(lead => ({
        tenant_id: profile.tenant_id,
        first_name: lead.first_name || null,
        last_name: lead.last_name || null,
        email: lead.email || null,
        phone: lead.phone || null,
        company_name: lead.company_name || null,
        location: lead.location || null,
        source: 'import' as const,
        status: 'new' as const,
        tags: lead.tags ? lead.tags.split(';').map(t => t.trim()) : [],
        metadata: {
          imported_at: new Date().toISOString(),
          original_data: lead
        }
      }));

      const { data: insertedLeads, error: insertError } = await supabase
        .from('leads')
        .insert(leadsForInsert)
        .select();

      if (insertError) throw insertError;

      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success(`Successfully imported ${insertedLeads?.length || 0} leads`);

      // Reset state
      setParsedLeads([]);
      setValidationResult(null);

      return insertedLeads;
    } catch (err: any) {
      console.error('Import error:', err);
      toast.error(err.message || 'Failed to import leads');
    } finally {
      setIsImporting(false);
    }
  };

  const reset = () => {
    setParsedLeads([]);
    setValidationResult(null);
  };

  return {
    isValidating,
    isImporting,
    parsedLeads,
    validationResult,
    parseCSV,
    validateLeads,
    importLeads,
    reset,
  };
}
